import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Clock, 
  DollarSign, 
  Calendar, 
  Edit, 
  Lock, 
  AlertTriangle,
  Save
} from 'lucide-react'
import { timesheetService, timesheetUtils } from '@/services/timesheetService'
import { projectService } from '@/services/projectService'
import { useTimer } from '@/contexts/TimerContext'
import toast from 'react-hot-toast'

interface EditableTimeEntry {
  id: string
  description: string
  startTime: string
  endTime: string
  projectId: string
  project: {
    id: string
    name: string
    color: string
  }
  duration: number
  isEdited?: boolean
}

interface ModificationSummary {
  modifiedEntriesCount: number
  totalHoursChange: number
  affectedDays: string[]
}

const TimesheetEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { refreshTimerData } = useTimer()
  
  // 狀態管理
  const [timesheetData, setTimesheetData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [summary, setSummary] = useState('')
  const [entries, setEntries] = useState<EditableTimeEntry[]>([])
  const [projects, setProjects] = useState<any[]>([])
  const [modificationSummary, setModificationSummary] = useState<ModificationSummary>({
    modifiedEntriesCount: 0,
    totalHoursChange: 0,
    affectedDays: []
  })

  // 載入數據
  useEffect(() => {
    if (id) {
      loadTimesheetData()
      loadProjects()
    }
  }, [id])

  const loadTimesheetData = async () => {
    try {
      setLoading(true)
      const data = await timesheetService.getEditableTimesheet(id!)
      setTimesheetData(data)
      setSummary(data.comments || '')
      
      // 轉換 entries 格式
      const editableEntries: EditableTimeEntry[] = data.entries.map((entry: any) => ({
        id: entry.id,
        description: entry.description || '',
        startTime: new Date(entry.startTime).toISOString().slice(0, 16),
        endTime: new Date(entry.endTime).toISOString().slice(0, 16),
        projectId: entry.projectId,
        project: entry.project,
        duration: entry.duration,
        isEdited: entry.isEdited || false
      }))
      setEntries(editableEntries)
    } catch (error) {
      console.error('Load timesheet error:', error)
      toast.error('載入申報資料失敗')
      navigate('/timesheets')
    } finally {
      setLoading(false)
    }
  }

  const loadProjects = async () => {
    try {
      const projectsData = await projectService.getProjects({ isActive: true })
      setProjects(projectsData)
    } catch (error) {
      console.error('Load projects error:', error)
    }
  }

  // 更新時間條目
  const updateEntry = (entryId: string, field: string, value: string) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === entryId) {
        const updatedEntry = { ...entry, [field]: value, isEdited: true }
        
        // 如果是時間相關字段，重新計算 duration
        if (field === 'startTime' || field === 'endTime') {
          const startTime = field === 'startTime' ? new Date(value) : new Date(updatedEntry.startTime)
          const endTime = field === 'endTime' ? new Date(value) : new Date(updatedEntry.endTime)
          updatedEntry.duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000)
        }
        
        // 如果是專案變更，更新專案資訊
        if (field === 'projectId') {
          const project = projects.find(p => p.id === value)
          if (project) {
            updatedEntry.project = {
              id: project.id,
              name: project.name,
              color: project.color
            }
          }
        }
        
        return updatedEntry
      }
      return entry
    }))
    
    updateModificationSummary()
  }

  // 更新修改摘要
  const updateModificationSummary = () => {
    const modifiedCount = entries.filter(entry => entry.isEdited).length
    const originalTotal = timesheetData?.totalHours || 0
    const currentTotal = entries.reduce((sum, entry) => sum + entry.duration, 0) / 3600
    const hoursChange = currentTotal - originalTotal
    
    const affectedDays = [...new Set(
      entries
        .filter(entry => entry.isEdited)
        .map(entry => entry.startTime.split('T')[0])
    )]
    
    setModificationSummary({
      modifiedEntriesCount: modifiedCount,
      totalHoursChange: hoursChange,
      affectedDays
    })
  }

  // 處理提交
  const handleSubmit = async () => {
    try {
      setSaving(true)
      
      // 準備提交數據
      const submitData = {
        entries: entries.map(entry => ({
          id: entry.id,
          description: entry.description,
          startTime: entry.startTime,
          endTime: entry.endTime,
          projectId: entry.projectId
        })),
        comments: summary
      }
      
      // 如果有修改，先更新申報
      if (modificationSummary.modifiedEntriesCount > 0) {
        await timesheetService.updateTimesheet(id!, submitData)
      }
      
      // 提交申報
      await timesheetService.submitEditedTimesheet(id!, { summary })
      
      // 刷新計時器頁面數據（包括週次鎖定狀態）
      await refreshTimerData()
      
      toast.success('工時申報提交成功！')
      navigate('/timesheets')
    } catch (error) {
      console.error('Submit timesheet error:', error)
      toast.error('提交失敗，請稍後再試')
    } finally {
      setSaving(false)
    }
  }

  // 處理取消
  const handleCancel = () => {
    navigate('/timesheets')
  }

  // 格式化週次範圍
  const formatWeekRange = (weekStart: string, weekEnd: string) => {
    return timesheetUtils.formatWeekRange(weekStart, weekEnd)
  }

  // 按日期分組條目
  const entriesByDate = entries.reduce((acc, entry) => {
    const date = entry.startTime.split('T')[0]
    if (!acc[date]) acc[date] = []
    acc[date].push(entry)
    return acc
  }, {} as Record<string, EditableTimeEntry[]>)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!timesheetData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">申報記錄不存在</h2>
          <p className="text-gray-600">請檢查申報ID是否正確</p>
        </div>
      </div>
    )
  }

  const currentTotalHours = entries.reduce((sum, entry) => sum + entry.duration, 0) / 3600
  const workingDays = Object.keys(entriesByDate).length

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="btn-ghost flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            返回
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">提交工時申報</h1>
            <p className="text-gray-600 mt-1">
              {timesheetData.user?.name} - {formatWeekRange(timesheetData.weekStart, timesheetData.weekEnd)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            {timesheetData.status === 'DRAFT' ? '草稿' : '已駁回'}
          </span>
        </div>
      </div>

      {/* 統計概覽區 */}
      <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">📊 統計概覽</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-gray-600">總工時</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{currentTotalHours.toFixed(1)} 小時</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-600">總金額</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">
              NT$ {Math.round(currentTotalHours * (timesheetData.user?.hourlyRate || 0)).toLocaleString()}
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-gray-600">工作天數</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{workingDays} 天</p>
          </div>
        </div>
      </div>

      {/* 數據可視化區 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 每日工時分布 */}
        <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 每日工時分布</h3>
          <div className="space-y-3">
            {Object.entries(entriesByDate).map(([date, dayEntries]) => {
              const dayTotal = dayEntries.reduce((sum, entry) => sum + entry.duration, 0) / 3600
              const maxHours = 12
              const percentage = Math.min((dayTotal / maxHours) * 100, 100)
              
              return (
                <div key={date} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {timesheetUtils.formatDateDisplay(date)}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-800 w-12 text-right">
                      {dayTotal.toFixed(1)}h
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* 專案時間分配 */}
        <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">🎯 專案時間分配</h3>
          <div className="space-y-3">
            {Object.values(
              entries.reduce((acc, entry) => {
                const projectId = entry.project.id
                if (!acc[projectId]) {
                  acc[projectId] = {
                    name: entry.project.name,
                    color: entry.project.color,
                    hours: 0
                  }
                }
                acc[projectId].hours += entry.duration / 3600
                return acc
              }, {} as Record<string, { name: string; color: string; hours: number }>)
            ).map((project, index) => {
              const percentage = (project.hours / currentTotalHours) * 100
              
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    <span className="text-sm text-gray-700">{project.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {percentage.toFixed(1)}%
                    </span>
                    <span className="text-sm font-medium text-gray-800 w-12 text-right">
                      {project.hours.toFixed(1)}h
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* 編輯模式切換區 */}
      <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">⚠️ 編輯提醒</h3>
          <button
            onClick={() => setEditMode(!editMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              editMode 
                ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {editMode ? <Lock className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
            {editMode ? '關閉編輯模式' : '啟用編輯模式'}
          </button>
        </div>
        
        <div className="space-y-2 text-sm text-gray-600">
          <p>• 啟用編輯模式後，所有修改都會被記錄在系統中</p>
          <p>• 編輯過的時間條目會顯示「已編輯」標記</p>
          <p>• 修改記錄將在審核時一併顯示給管理員</p>
        </div>
      </div>

      {/* 工時明細表格 */}
      <div className="bg-white rounded-xl shadow-soft border border-primary-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">📝 工時明細</h3>
        </div>
        
        <div className="p-6 space-y-6">
          {Object.entries(entriesByDate).sort().map(([date, dayEntries]) => {
            const dayTotal = dayEntries.reduce((sum, entry) => sum + entry.duration, 0) / 3600
            
            return (
              <div key={date} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">
                      📅 {timesheetUtils.formatDateDisplay(date)}
                    </h4>
                    <span className="text-sm text-gray-600">
                      總計 {dayTotal.toFixed(1)} 小時
                    </span>
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  {dayEntries.map((entry) => (
                    <div 
                      key={entry.id} 
                      className={`border rounded-lg p-4 ${
                        entry.isEdited ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.project.color }}
                          />
                          {editMode ? (
                            <select
                              value={entry.projectId}
                              onChange={(e) => updateEntry(entry.id, 'projectId', e.target.value)}
                              className="border border-gray-300 rounded px-2 py-1 text-sm"
                            >
                              {projects.map(project => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="font-medium text-gray-900">{entry.project.name}</span>
                          )}
                          {entry.isEdited && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                              已編輯
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          {editMode ? (
                            <>
                              <input
                                type="datetime-local"
                                value={entry.startTime}
                                onChange={(e) => updateEntry(entry.id, 'startTime', e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1"
                              />
                              <span>-</span>
                              <input
                                type="datetime-local"
                                value={entry.endTime}
                                onChange={(e) => updateEntry(entry.id, 'endTime', e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1"
                              />
                            </>
                          ) : (
                            <span>
                              {new Date(entry.startTime).toLocaleTimeString('zh-TW', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })} - {new Date(entry.endTime).toLocaleTimeString('zh-TW', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                          )}
                          <span className="font-medium">
                            ({(entry.duration / 3600).toFixed(1)}h)
                          </span>
                          <span>
                            NT$ {Math.round(entry.duration / 3600 * (timesheetData.user?.hourlyRate || 0))}
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">工作描述：</label>
                        {editMode ? (
                          <textarea
                            value={entry.description}
                            onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                            placeholder="請描述本時段的工作內容..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                            rows={2}
                          />
                        ) : (
                          <p className="text-gray-700 text-sm">
                            {entry.description || '無描述'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* 工作總結編輯區 */}
      <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📝 本週工作總結</h3>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="請描述本週的主要工作內容、完成的任務、遇到的問題以及下週的計劃..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <div className="mt-2 text-sm text-gray-500">
          💡 提示：詳細的工作總結有助於提高審核效率
        </div>
      </div>

      {/* 編輯記錄提醒區 */}
      {editMode && modificationSummary.modifiedEntriesCount > 0 && (
        <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 編輯記錄追蹤</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 p-3 rounded-lg">
              <AlertTriangle className="w-4 h-4" />
              <span>您已啟用編輯模式，所有修改都會被記錄</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-blue-900 font-medium">已修改條目</p>
                <p className="text-blue-700">{modificationSummary.modifiedEntriesCount} 個</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-green-900 font-medium">總工時變化</p>
                <p className="text-green-700">
                  {modificationSummary.totalHoursChange > 0 ? '+' : ''}
                  {modificationSummary.totalHoursChange.toFixed(1)} 小時
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-purple-900 font-medium">影響日期</p>
                <p className="text-purple-700">{modificationSummary.affectedDays.length} 天</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 底部操作區 */}
      <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📤 提交申報</h3>
        
        <div className="space-y-3 text-sm text-gray-600 mb-6">
          <div className="flex items-start gap-2">
            <span>⚠️</span>
            <span><strong>提交前確認：</strong></span>
          </div>
          <ul className="ml-6 space-y-1">
            <li>• 所有工時記錄準確無誤</li>
            <li>• 工作描述詳細完整</li>
            <li>• 了解提交後無法再次修改</li>
          </ul>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleCancel}
            className="btn-ghost"
            disabled={saving}
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary flex items-center gap-2"
            disabled={saving}
          >
            <Save className="w-4 h-4" />
            {saving ? '提交中...' : '提交申報'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TimesheetEditPage
